import {LegalAgreementsWithoutProfiles} from "../models/PartnerResource";
import {PartnerDashboardProfile} from "../models/ProfileResponse";
import {ConditionResultResponse} from "../../Monitoring/ConditionResultResponse";
import moment from "moment/moment";

export const dashboardVariables = {
    PARTNER_DASHBOARD_DEFAULT_ROW: 100
}
export const PARTNER_DASHBOARD_PAGE_SIZES = [15, 25, 50, 100];
export const DEFAULT_ACTIVE_INDEX = 0;
export const PARTNER_DASHBOARD_MIN_ROWS = 0;
export const NATIONAL_SAMPLES_DEFAULT_PAGE_SIZE = PARTNER_DASHBOARD_PAGE_SIZES[0];

export enum SortType {
    ascendingName = "ascendingName",
    descendingName = "descendingName",
    ascendingLastModifiedByName = "ascendingLastModifiedByName",
    descendingLastModifiedByName = "descendingLastModifiedByName",
    ascendingLastModifiedDate = "ascendingLastModifiedDate",
    descendingLastModifiedDate = "descendingLastModifiedDate",
    ascendingProfileConditions = "ascendingProfileConditions",
    descendingProfileConditions = "descendingProfileConditions",
    ascendingProfileName = "ascendingProfileName",
    descendingProfileName = "descendingProfileName",
    ascendingLegalAgreementName = "ascendingLegalAgreementName",
    descendingLegalAgreementName = "descendingLegalAgreementName",
    ascendingLegalAgreementId = "ascendingLegalAgreementId",
    descendingLegalAgreementId = "descendingLegalAgreementId",
    ascendingDaysActive = "ascendingDaysActive",
    descendingDaysActive = "descendingDaysActive",
    ascendingAnnualReportSentDate = "ascendingAnnualReportSentDate",
    descendingAnnualReportSentDate = "descendingAnnualReportSentDate",
    ascendingLastMeetingEndDate = "ascendingLastMeetingEndDate",
    descendingLastMeetingEndDate = "descendingLastMeetingEndDate",
    ascendingPortfolioReserve = "ascendingPortfolioReserve",
    descendingPortfolioReserve = "descendingPortfolioReserve",
    ascendingOrdinal = "ascendingOrdinal",
}

export const conditionFilterOptions = [
    'Risk Assets Underfunded',
    'Risk Control Assets Underfunded',
    'All Column Insufficient',
    'Investable Column Insufficient',
    'Standalone Update Overdue',
    'Equity Comp Update Overdue',
    'GDWM Annual Report'
];

export type Column = {
    id: string;
};

export type FilterProfileOptions =
    "activeClients"
    | "archivedClients"
    | "clientsWithConditions"
    | "legalAgreementsWithoutProfile";

const compareDates = (date1: string | null, date2: string | null) => {
    if (moment(date1).isBefore(moment(date2))) {
        return -1;
    }
    const comparisonResult = moment(date1).isAfter(moment(date2)) ? 1 : 0;
    return comparisonResult;
}

const compareDatesWithNulls = (date1: string | null, date2: string | null, ascending: boolean = true) => {
    if (date1 === null && date2 === null) {
        return 0;
    }
    if (date1 === null) {
        return 1; // Nulls always come at the end
    }
    if (date2 === null) {
        return -1; // Nulls always come at the end
    }
    let comparisonResult = 0;
    if (moment(date1).isBefore(moment(date2))) {
        comparisonResult = ascending ? -1 : 1;
    } else if (moment(date1).isAfter(moment(date2))) {
        comparisonResult = ascending ? 1 : -1;
    }
    return comparisonResult;
};

const compareProfileConditions =
    (profileOne: PartnerDashboardProfile, profileTwo: PartnerDashboardProfile, sortType: SortType, profileConditions?: ConditionResultResponse[]) => {

        const conditionResultResponseOne: ConditionResultResponse | undefined = profileConditions?.find(element => {
            return element.profileId === profileOne.id
        });

        const conditionResultResponseTwo: ConditionResultResponse | undefined = profileConditions?.find(element => {
            return element.profileId === profileTwo.id
        });

        const conditionsLengthOne = conditionResultResponseOne?.conditions?.length ?? 0;
        const conditionsLengthTwo = conditionResultResponseTwo?.conditions?.length ?? 0;

        if ((!conditionResultResponseOne || conditionsLengthOne === 0) &&
            (!conditionResultResponseTwo || conditionsLengthTwo === 0)) {
            return profileOne.displayName.localeCompare(profileTwo.displayName);
        }

        if (!conditionResultResponseOne)
            return 1;

        if (!conditionResultResponseTwo)
            return -1;

        if (conditionsLengthOne === conditionsLengthTwo) {
            return profileOne.displayName.localeCompare(profileTwo.displayName);
        }

        const conditionOne = conditionsLengthTwo < conditionsLengthOne ? 1 : -1;
        const conditionTwo = conditionsLengthTwo > conditionsLengthOne ? 1 : -1;

        if (conditionResultResponseTwo && conditionResultResponseOne) {
            return sortType === "ascendingProfileConditions" ? conditionOne : conditionTwo;
        }
        return 0;
    }

export const sortLegalAgreements = (
    sortType: SortType,
    data: LegalAgreementsWithoutProfiles[]
): LegalAgreementsWithoutProfiles[] => {
    switch (sortType) {
        case SortType.ascendingProfileName:
            data.sort((a, b) => a.profileName.localeCompare(b.profileName));
            break;
        case SortType.descendingProfileName:
            data.sort((a, b) => b.profileName.localeCompare(a.profileName));
            break;
        case SortType.ascendingLegalAgreementName:
            data.sort((a, b) => a.legalAgreementName.localeCompare(b.legalAgreementName));
            break;
        case SortType.descendingLegalAgreementName:
            data.sort((a, b) => b.legalAgreementName.localeCompare(a.legalAgreementName));
            break;
        case SortType.ascendingLegalAgreementId:
            data.sort((a, b) => a.legalAgreementId.localeCompare(b.legalAgreementId));
            break;
        case SortType.descendingLegalAgreementId:
            data.sort((a, b) => b.legalAgreementId.localeCompare(a.legalAgreementId));
            break;
        case SortType.ascendingDaysActive:
            data.sort((a, b) => a.daysActive - b.daysActive);
            break;
        case SortType.descendingDaysActive:
            data.sort((a, b) => b.daysActive - a.daysActive);
            break;
        case SortType.ascendingOrdinal:
            data.sort((a, b) => a.ordinal! - b.ordinal!);
            break;
    }
    return data;
}

export const sortProfiles = (sortType: SortType, data: PartnerDashboardProfile[], profileConditions?: ConditionResultResponse[]) => {
    switch (sortType) {
        case SortType.ascendingName:
            data.sort((a, b) => a.displayName.localeCompare(b.displayName));
            break;
        case SortType.descendingName:
            data.sort((a, b) => b.displayName.localeCompare(a.displayName));
            break;
        case SortType.ascendingLastModifiedByName:
            data.sort((a, b) => a.lastModifiedByName.localeCompare(b.lastModifiedByName));
            break;
        case SortType.descendingLastModifiedByName:
            data.sort((a, b) => b.lastModifiedByName.localeCompare(a.lastModifiedByName));
            break;
        case SortType.ascendingProfileConditions:
            data.sort((a, b) => compareProfileConditions(a, b, SortType.ascendingProfileConditions, profileConditions));
            break;
        case SortType.descendingProfileConditions:
            data.sort((a, b) => compareProfileConditions(a, b, SortType.descendingProfileConditions, profileConditions));
            break;
        case SortType.ascendingLastModifiedDate:
            data.sort((a, b) => compareDates(a.lastModifiedDate, b.lastModifiedDate));
            break;
        case SortType.descendingLastModifiedDate:
            data.sort((a, b) => compareDates(b.lastModifiedDate, a.lastModifiedDate));
            break;
        case SortType.ascendingAnnualReportSentDate:
            data.sort((a, b) => compareDatesWithNulls(a.annualReportSentDate, b.annualReportSentDate));
            break;
        case SortType.descendingAnnualReportSentDate:
            data.sort((a, b) => compareDatesWithNulls(a.annualReportSentDate, b.annualReportSentDate, false));
            break;
        case SortType.ascendingLastMeetingEndDate:
            data.sort((a, b) => compareDatesWithNulls(a.lastMeetingEndDate, b.lastMeetingEndDate));
            break;
        case SortType.descendingLastMeetingEndDate:
            data.sort((a, b) => compareDatesWithNulls(a.lastMeetingEndDate, b.lastMeetingEndDate, false));
            break;
        case SortType.ascendingPortfolioReserve:
            data.sort((a, b) => {
                if (a.activePortfolioReserve === b.activePortfolioReserve) {
                    return 0;
                }
                return a.activePortfolioReserve ? -1 : 1;
            });
            break;
        case SortType.descendingPortfolioReserve:
            data.sort((a, b) => {
                if (a.activePortfolioReserve === b.activePortfolioReserve) {
                    return 0;
                }
                return a.activePortfolioReserve ? 1 : -1;
            });
            break;
        case SortType.ascendingOrdinal:
            data.sort((a, b) => a.ordinal! - b.ordinal!);
            break;
        case SortType.ascendingDaysActive:
            data.sort((a, b) => a.daysActive! - b.daysActive!);
            break;
        case SortType.descendingDaysActive:
            data.sort((a, b) => b.daysActive! - a.daysActive!);
            break;
    }
    return data;
};

export const lastModifiedSortType = (sortType: string): SortType | undefined => {

    let lastSortType: SortType = SortType.ascendingName;
    switch (sortType) {
        case SortType.ascendingName:
            lastSortType = SortType.ascendingName;
            break;
        case SortType.descendingName:
            lastSortType = SortType.descendingName;
            break;
        case SortType.ascendingLastModifiedByName:
            lastSortType = SortType.ascendingLastModifiedByName;
            break;
        case SortType.descendingLastModifiedByName:
            lastSortType = SortType.descendingLastModifiedByName;
            break;
        case SortType.ascendingProfileConditions:
            lastSortType = SortType.ascendingProfileConditions;
            break;
        case SortType.descendingProfileConditions:
            lastSortType = SortType.descendingProfileConditions;
            break;
        case SortType.ascendingLastModifiedDate:
            lastSortType = SortType.ascendingLastModifiedDate;
            break;
        case SortType.descendingLastModifiedDate:
            lastSortType = SortType.descendingLastModifiedDate;
            break;
        case SortType.ascendingAnnualReportSentDate:
            lastSortType = SortType.ascendingAnnualReportSentDate;
            break;
        case SortType.descendingAnnualReportSentDate:
            lastSortType = SortType.descendingAnnualReportSentDate;
            break;
        case SortType.ascendingLastMeetingEndDate:
            lastSortType = SortType.ascendingLastMeetingEndDate;
            break;
        case SortType.descendingLastMeetingEndDate:
            lastSortType = SortType.descendingLastMeetingEndDate;
            break;
        case SortType.ascendingPortfolioReserve:
            lastSortType = SortType.ascendingPortfolioReserve;
            break;
        case SortType.descendingPortfolioReserve:
            lastSortType = SortType.descendingPortfolioReserve;
            break;
        case SortType.ascendingDaysActive:
            lastSortType = SortType.ascendingDaysActive
            break;
        case SortType.descendingDaysActive:
            lastSortType = SortType.descendingDaysActive
            break;
    }
    return lastSortType;
}

    export const lastModifiedSortTypeLegalAgreement = (
        sortType: string,
    ): SortType | undefined => {
        let lastSortType : SortType = SortType.ascendingProfileName;
        switch (sortType) {
            case SortType.ascendingProfileName:
                lastSortType = SortType.ascendingProfileName
                break;
            case SortType.descendingProfileName:
                lastSortType = SortType.descendingProfileName
                break;
            case SortType.ascendingLegalAgreementName:
                lastSortType = SortType.ascendingLegalAgreementName
                break;
            case SortType.descendingLegalAgreementName:
                lastSortType = SortType.descendingLegalAgreementName
                break;
            case SortType.ascendingLegalAgreementId:
                lastSortType = SortType.ascendingLegalAgreementId
                break;
            case SortType.descendingLegalAgreementId:
                lastSortType = SortType.descendingLegalAgreementId
                break;
            case SortType.ascendingDaysActive:
                lastSortType = SortType.ascendingDaysActive
                break;
            case SortType.descendingDaysActive:
                lastSortType = SortType.descendingDaysActive
                break;
        }
        return lastSortType;
    }


export const sortLAMapping: Record<string, { ascending: SortType; descending: SortType }> = {
    profileName: {
        ascending: SortType.ascendingProfileName,
        descending: SortType.descendingProfileName,
    },
    legalAgreementName: {
        ascending: SortType.ascendingLegalAgreementName,
        descending: SortType.descendingLegalAgreementName,
    },
    legalAgreementId: {
        ascending: SortType.ascendingLegalAgreementId,
        descending: SortType.descendingLegalAgreementId,
    },
    daysActive: {
        ascending: SortType.ascendingDaysActive,
        descending: SortType.descendingDaysActive,
    },
};

export const sortClientMapping: Record<string, { ascending: SortType; descending: SortType }> = {
    profileAlerts: {
        ascending: SortType.ascendingProfileConditions,
        descending: SortType.descendingProfileConditions,
    },
    displayName: {
        ascending: SortType.ascendingName,
        descending: SortType.descendingName,
    },
    lastModifiedByName: {
        ascending: SortType.ascendingLastModifiedByName,
        descending: SortType.descendingLastModifiedByName,
    },
    lastModifiedDate: {
        ascending: SortType.ascendingLastModifiedDate,
        descending: SortType.descendingLastModifiedDate,
    },
    annualReportSentDate: {
        ascending: SortType.ascendingAnnualReportSentDate,
        descending: SortType.descendingAnnualReportSentDate,
    },
    lastMeetingEndDate: {
        ascending: SortType.ascendingLastMeetingEndDate,
        descending: SortType.descendingLastMeetingEndDate,
    },
    portfolioReserve: {
        ascending: SortType.ascendingPortfolioReserve,
        descending: SortType.descendingPortfolioReserve,
    },
    daysActive: {
        ascending: SortType.ascendingDaysActive,
        descending: SortType.descendingDaysActive,
    },
};